import { SomeJSONSchema } from "ajv/dist/types/json-schema";
import { JSX } from "preact";
import { Country } from "react-phone-number-input";

export type FormTypes = "dynamic" | "serve";

// Describes the type of the form control used to accept user input
export type FormFieldInputType =
  | "text"
  | "nps"
  | "email"
  | "agree_disagree"
  | "checkbox"
  | "radio"
  | "textarea"
  | "select"
  | "multi-select"
  | "date"
  | "dob"
  | "file"
  | "ranking"
  | "star_rating"
  | "number"
  | "phone";

export type FormAttribute = {
  token: string;
  attribute_metadata_key: string;
};

export enum FormErrorMsg {
  MISSING_TOKENS = "Sorry, we couldn't process your response. One or more fields were missing. Please try again, and if the problem continues, contact the form owner.",
  UNKNOWN_ERROR = "Sorry, we couldn't process your response due to an unknown error. Please try again, and if the problem continues, contact the form owner.",
  SUBMISSION_FAILED = "Sorry, we were unable to process your response. Please check your internet connection and try again. If the problem continues, contact the form owner.",
  FORM_ERROR = "Sorry, we were unable to process your response. This form may have expired. Contact the form owner for help.",
  INVALID_FORM_ERROR = "Sorry, we were unable to process your response due to a technical error. Please try again, and if the problem continues, contact the form owner.",
  INVALID_FORM_INSTANCE_ERROR = "Sorry, we were unable to process your response. Please check the URL in your browser matches the one you were sent. If the problem continues, contact the form owner.",
  FORM_RESPONSE_EXISTS_ERROR = "Sorry, we've already received a response to this form. If this isn't correct, please contact the form owner.",
  FORM_SUBMISSION_IGNORED = "Sorry, we couldn't process your response as all fields were missing. Please try again, and if the problem continues, contact the form owner.",
}

// `FormBlocks` represent an item that a user can add to a form.
// This may be a question on the form, or supporting content such as a paragraph of text, an image, video

// Questions that write data to a single attribute
export type BasicQuestionType =
  | "email"
  | "short_text"
  | "long_text"
  | "first_name"
  | "last_name"
  | "dropdown"
  | "multi_choice"
  | "checkboxes"
  | "yes_no"
  | "nps"
  | "scale"
  | "ranking"
  | "phone"
  | "star_rating"
  | "legal"
  | "date"
  | "date_of_birth"
  | "number"
  | "file_upload";

export type TextInputQuestionTypes = Extract<
  BasicQuestionType,
  "email" | "short_text" | "first_name" | "last_name" | "number"
>;

// Blocks used to display supporting content, and which don't accept user responses
// eg. text, images, or video
export type ContentType = "text_content" | "image" | "video";

export type FormBlockType = BasicQuestionType | ContentType;

export type BaseFormBlock = {
  id: number;
  block_type: FormBlockType;
  title: string | null;
  description: string | null;
  error: string | null;
  placeholder?: string | null;
  conditions?: FormConditions;
  required: boolean;
  attributes?: FormAttribute[];
  pageIdx?: number;
};

// This type stores additional data used to construct a form block, supplied through the form builder UI.
// This includes its schema, and additional properties that may be used to customise the question.
// eg. date formatting, custom scale labels
export type QuestionBlockParams<S = undefined, E = {}> = {
  url_name?: string;
  schema: S;
} & E;

export type TextContent = BaseFormBlock & {
  block_type: "text_content";
  params: Record<string, unknown>;
};

export type ImageContent = BaseFormBlock & {
  block_type: "image";
  params: { img_src: string };
};

export type VideoContent = BaseFormBlock & {
  block_type: "video";
  params: { video_url: string; video_provider: string };
};

export type ContentBlock = TextContent | ImageContent | VideoContent;

export type StringEnumSchema = SomeJSONSchema & {
  type: "string";
  enum: string[];
  minItems?: number;
};

export type StringEnumSchemaWithOther = SomeJSONSchema & {
  anyOf: [
    {
      type: "string";
      enum: string[];
      minItems?: number;
    },
    { type: "string"; pattern: string }
  ];
};

export type TextSchema = SomeJSONSchema & {
  type: "string";
  format?: string;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
};

export type IntegerSchema = SomeJSONSchema & {
  type: "integer";
  minimum?: number;
  maximum?: number;
};

export type StringArraySchema = SomeJSONSchema & {
  type: "array";
  minItems?: number;
  uniqueItems?: true;
  items: {
    type: "string";
    enum?: string[];
    maxLength?: number;
    minLength?: number;
    pattern?: string;
  };
};

export type StringArrayEnumSchema = SomeJSONSchema & {
  type: "array";
  minItems?: number;
  uniqueItems?: true;
  items: {
    type: "string";
    enum?: string[];
    maxLength?: number;
    minLength?: number;
    pattern?: string;
  };
};

export type BooleanSchema = SomeJSONSchema & {
  type: "boolean";
};

export type DateSchema = SomeJSONSchema & {
  type: "string";
  format: "date-time";
  formatExclusiveMaximum?: string;
  formatExclusiveMinimum?: string;
};

// Conditions for showing/hiding a question, or short-circuiting a form
export type FormConditions = {
  is?: { [block_id: string]: string[] };
  not?: { [block_id: string]: string[] };
  gte?: { [block_id: string]: number | string | Date };
  lte?: { [block_id: string]: number | string | Date };
};

// Backwards compatibility for field with "Other" option
export type OldMultiChoiceParams = QuestionBlockParams<
  StringEnumSchema,
  {
    allow_other?: boolean;
  }
>;

export type MultiChoiceParams = QuestionBlockParams<
  StringEnumSchemaWithOther | StringEnumSchema,
  {
    options: string[];
    allow_other?: boolean;
  }
>;

export type OldCheckboxParams = QuestionBlockParams<
  StringArrayEnumSchema,
  {
    allow_other?: boolean;
  }
>;

export type CheckboxParams = QuestionBlockParams<
  StringArrayEnumSchema,
  {
    options: string[];
    allow_other?: boolean;
  }
>;

// Question types
export type EmailQuestion = BaseFormBlock & {
  block_type: "email";
  params: QuestionBlockParams<TextSchema>;
};

export type ShortTextQuestion = BaseFormBlock & {
  block_type: "short_text";
  params: QuestionBlockParams<TextSchema>;
};

export type LongTextQuestion = BaseFormBlock & {
  block_type: "long_text";
  params: QuestionBlockParams<TextSchema>;
};

export type FirstNameQuestion = BaseFormBlock & {
  block_type: "first_name";
  params: QuestionBlockParams<TextSchema>;
};

export type LastNameQuestion = BaseFormBlock & {
  block_type: "last_name";
  params: QuestionBlockParams<TextSchema>;
};

export type DropdownQuestion = BaseFormBlock & {
  block_type: "dropdown";
  default_value?: string;
  params: MultiChoiceParams | OldMultiChoiceParams;
};

export type MultipleChoiceQuestion = BaseFormBlock & {
  block_type: "multi_choice";
  params: MultiChoiceParams | OldMultiChoiceParams;
};

export type CheckboxQuestion = BaseFormBlock & {
  block_type: "checkboxes";
  params: CheckboxParams | OldCheckboxParams;
};

export type YesNoQuestion = BaseFormBlock & {
  block_type: "yes_no";
  params: QuestionBlockParams<StringEnumSchema>;
};

export type NPSQuestion = BaseFormBlock & {
  block_type: "nps";
  params: QuestionBlockParams<IntegerSchema>;
};

export type ScaleQuestion = BaseFormBlock & {
  block_type: "scale";
  params: QuestionBlockParams<
    IntegerSchema,
    {
      min_label?: string;
      median_label?: string;
      max_label?: string;
    }
  >;
};

export type RankingQuestion = BaseFormBlock & {
  block_type: "ranking";
  params: QuestionBlockParams<StringArraySchema, { labels: string[] }>;
};

export type PhoneQuestion = BaseFormBlock & {
  block_type: "phone";
  params: QuestionBlockParams<TextSchema, { default_country?: Country }>;
};

export type StarRatingQuestion = BaseFormBlock & {
  block_type: "star_rating";
  params: QuestionBlockParams<IntegerSchema, { rating_type?: "stars" }>;
};

export type LegalQuestion = BaseFormBlock & {
  block_type: "legal";
  params: QuestionBlockParams<BooleanSchema>;
};

export type DateQuestion = BaseFormBlock & {
  block_type: "date";
  params: QuestionBlockParams<DateSchema, { formatString?: string }>;
};

export type DateOfBirthQuestion = BaseFormBlock & {
  block_type: "date_of_birth";
  params: QuestionBlockParams<DateSchema, { formatString?: string }>;
};

export type FileUploadQuestion = BaseFormBlock & {
  block_type: "file_upload";
  params: QuestionBlockParams<
    StringArraySchema,
    {
      allowed_file_types: string[];
      max_file_bytes: number;
      max_file_count: number;
    }
  >;
};

export type NumberQuestion = BaseFormBlock & {
  block_type: "number";
  params: QuestionBlockParams<IntegerSchema, { range_required?: boolean }>;
};

export type CheckboxBlock = CheckboxQuestion;

export type MultiChoiceBlock =
  | DropdownQuestion
  | MultipleChoiceQuestion
  | YesNoQuestion;

export type BasicQuestionBlock =
  | EmailQuestion
  | ShortTextQuestion
  | LongTextQuestion
  | FirstNameQuestion
  | LastNameQuestion
  | DropdownQuestion
  | MultipleChoiceQuestion
  | CheckboxQuestion
  | YesNoQuestion
  | NPSQuestion
  | ScaleQuestion
  | RankingQuestion
  | StarRatingQuestion
  | LegalQuestion
  | DateQuestion
  | DateOfBirthQuestion
  | PhoneQuestion
  | NumberQuestion
  | FileUploadQuestion;

export type FormBlock = ContentBlock | BasicQuestionBlock;
export type QuestionBlock = BasicQuestionBlock;

// The FormField type represents a question + state
export type BaseFieldState<
  I extends FormFieldInputType = "text",
  T = string
> = {
  field_key: string; // Attribute token or block ID
  input_type: I;
  writeToAttribute: string | null;
  status: "pending" | "input" | "invalid";
  visible: boolean;
  currentValue: T | null | undefined;
  required: boolean;
  validationErrors: string[] | null;
  pageIdx?: number;
  isPrefilled: boolean;
};

export type TextField = (
  | ShortTextQuestion
  | FirstNameQuestion
  | LastNameQuestion
  | NumberQuestion
) &
  BaseFieldState;
export type TextAreaField = LongTextQuestion & BaseFieldState<"textarea">;
export type ScaleField = ScaleQuestion &
  BaseFieldState<"agree_disagree", string>;
export type CheckBoxField = LegalQuestion & BaseFieldState<"checkbox", boolean>;
export type SelectField = DropdownQuestion & BaseFieldState<"select", string>;
export type MultiSelectField = CheckboxQuestion &
  BaseFieldState<"multi-select", Set<string>>;
export type EmailField = EmailQuestion & BaseFieldState<"email">;
export type NPSField = NPSQuestion & BaseFieldState<"nps", string>;
export type RadioField = MultipleChoiceQuestion &
  BaseFieldState<"radio", string>;
export type DateField = DateQuestion & BaseFieldState<"date", Date>;
export type DateOfBirthField = DateOfBirthQuestion &
  BaseFieldState<"dob", Date>;
export type RankingField = RankingQuestion & BaseFieldState<"ranking", string>;
export type PhoneField = PhoneQuestion & BaseFieldState<"phone", string>;
export type StarRatingField = StarRatingQuestion &
  BaseFieldState<"star_rating", string>;
export type FileField = FileUploadQuestion & BaseFieldState<"file", string[]>;

export type FormField =
  | TextField
  | TextAreaField
  | ScaleField
  | CheckBoxField
  | SelectField
  | MultiSelectField
  | EmailField
  | NPSField
  | RadioField
  | DateField
  | DateOfBirthField
  | PhoneField
  | StarRatingField
  | RankingField
  | FileField;

export type FieldRecord = Record<string, FormField>;

export type FormValue = string | boolean | string[] | Date;

export type FormPageType = "intro" | "body" | "finish";

export type IntroPage = {
  id: number;
  page_type: "intro";
  blocks: FormBlock[];
};

export type FinishPage = {
  id: number;
  page_type: "finish";
  blocks: FormBlock[];
};

export type BodyPage = {
  id: number;
  page_type: "body";
  blocks: FormBlock[];
};

export type FormPage = IntroPage | BodyPage | FinishPage;

export type ThemeData = {
  brand_primary: string; // Brand primary color in hex format
  bg_color: string; // Background color in hex format
  bg_image?: string; // Background image uuid
  title_text: string; // Title text color in hex format
  desc_text: string; // Description text color in hex format
  button_text: string; // Button text color in hex format
  title_align: "left" | "center" | "right"; // Title text alignment
  text_align: "left" | "center" | "right"; // Body text alignment
  custom_font?: string;
};

export type Form = {
  token: string;
  title: string;
  description: string;
  intro_title: string;
  intro_message: string;
  privacy_url?: string;
  privacy_url_label?: string;
  finish_title: string;
  finish_message: string;
  finish_url_label?: string;
  finish_url?: string;
  logo?: string;
  favicon?: string;
  extra_css?: string;
  pages: FormPage[];
  skip_conditions: FormConditions;
  archived: boolean;
  paused: boolean;
  published_version: number;
  published_at?: string | Date;
  theme: ThemeData;
  form_type?: FormTypes;
  prefill_fields?: string[];
  url_mappings?: Record<string, string>; // Map of field tokens to url param names
};

export type FormStatus =
  | { status: "pending" }
  | { status: "invalid" }
  | { status: "valid" }
  | { status: "submitting" }
  | { status: "failed"; error: string }
  | { status: "complete" };

export type FormState = {
  currentPage: FormPage;
  pages: FormPage[];
  visiblePages: FormPage[];
  formState: FormStatus;
  fields: FieldRecord;
  formId: string;
  userAgent: string;
  formInstance?: string;
  submitDisabled: boolean;
  formDefinitionIsValid: boolean;
  livePreview: boolean;
  urlParamErrors: Record<string, string[]>; // Map of url param names to error messages
  fileBlobs?: Record<string, FileBlob>;
};

export type BasicFormEvent =
  | JSX.TargetedEvent<HTMLInputElement>
  | JSX.TargetedEvent<HTMLSelectElement>
  | JSX.TargetedEvent<HTMLTextAreaElement>
  | JSX.TargetedEvent<HTMLButtonElement>;

export type FormResponseData = {
  userAgent: string;
  formId: string;
  formInstance: string | undefined;
};

export type FileBlob = {
  key: string;
  url: string;
  file: File;
};
